import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X, ShieldCheck } from "lucide-react";
import { EASE } from "./motionPrimitives";

const LINKS = [
    { label: "Services", href: "#services", id: "nav-link-services" },
    { label: "Approach", href: "#approach", id: "nav-link-process" },
    { label: "Results", href: "#results", id: "nav-link-results" },
    { label: "Testimonials", href: "#testimonials", id: "nav-link-testimonials" },
    { label: "Pricing", href: "#pricing", id: "nav-link-pricing" },
    { label: "FAQ", href: "#faq", id: "nav-link-faq" },
];

export const scrollToHash = (hash) => {
    const el = document.querySelector(hash);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -76 });
    else el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (e, href) => {
        e.preventDefault();
        setOpen(false);
        setTimeout(() => scrollToHash(href), open ? 250 : 0);
    };

    return (
        <>
            <header
                data-testid="site-navbar"
                className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                    scrolled
                        ? "bg-navy/90 backdrop-blur-xl border-b border-gold/20 shadow-lg shadow-navy-deep/40"
                        : "bg-navy/60 backdrop-blur-md border-b border-white/5"
                }`}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
                    <a
                        href="#"
                        data-testid="nav-brand-logo"
                        onClick={(e) => {
                            e.preventDefault();
                            if (window.__lenis)
                                window.__lenis.scrollTo(0, { offset: 0 });
                            else window.scrollTo({ top: 0, behavior: "smooth" });
                            setOpen(false);
                        }}
                        className="flex items-center"
                        aria-label="SMART NET home"
                    >
                        <img
                            src="/logos/smartnet-reversed-transparent.webp"
                            alt="SMART NET"
                            className="h-8 sm:h-10 w-auto"
                        />
                    </a>

                    <nav className="hidden lg:flex items-center gap-8">
                        {LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                data-testid={l.id}
                                onClick={(e) => go(e, l.href)}
                                className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist hover:text-gold transition-colors"
                            >
                                {l.label}
                            </a>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <a
                            href="/admin"
                            data-testid="nav-admin-view-button"
                            className="hidden sm:inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/15 text-mist hover:text-gold hover:border-gold/50 transition-colors"
                            title="Admin lead inbox"
                        >
                            <ShieldCheck size={17} />
                        </a>
                        <a
                            href="#contact"
                            data-testid="nav-cta-button"
                            onClick={(e) => go(e, "#contact")}
                            className="btn-shine hidden sm:inline-flex items-center gap-2 bg-gold text-navy font-bold text-sm px-6 py-3 rounded-full hover:bg-gold-hover transition-colors"
                        >
                            Book a strategy call
                            <ArrowUpRight size={16} />
                        </a>
                        <button
                            data-testid="nav-mobile-toggle"
                            onClick={() => setOpen(!open)}
                            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/15 text-white"
                            aria-label="Toggle menu"
                        >
                            {open ? <X size={18} /> : <Menu size={18} />}
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-40 bg-navy-deep/97 backdrop-blur-2xl lg:hidden"
                    >
                        <div className="h-full flex flex-col justify-center px-8 gap-2">
                            {LINKS.map((l, i) => (
                                <motion.a
                                    key={l.href}
                                    href={l.href}
                                    data-testid={`${l.id}-mobile`}
                                    onClick={(e) => go(e, l.href)}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.06 * i, duration: 0.5, ease: EASE }}
                                    className="font-display text-4xl font-bold text-white py-3 border-b border-white/10"
                                >
                                    {l.label}
                                </motion.a>
                            ))}
                            <motion.a
                                href="#contact"
                                data-testid="nav-mobile-cta"
                                onClick={(e) => go(e, "#contact")}
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4, duration: 0.5, ease: EASE }}
                                className="mt-8 inline-flex items-center justify-center gap-2 bg-gold text-navy font-bold px-8 py-4 rounded-full"
                            >
                                Book a strategy call
                                <ArrowUpRight size={18} />
                            </motion.a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
